import React, { useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import ActivityMap from './ActivityMap';
import ActivityStreamsChart from './ActivityStreamsChart';
import PowerCurveChart from './PowerCurveChart';
import { formatDuration } from '../utils/workouts';
import { formatDistance, formatPace } from '../utils/activities';
import './ActivityDetails.css';

// Выполненная тренировка: карта (если есть GPS-трек; у заездов MyWhoosh и Zwift его нет),
// итоги и графики телеметрии.
// Курсор графика показывает точку на карте. Для бега вместо скорости — темп.
// Из FIT — то же, что у заездов ROUVY: IF/TSS, датчики и кривая мощности
const ActivityDetails = ({ workout }) => {
  const { t } = useLanguage();
  const [cursor, setCursor] = useState(null);
  const summary = workout.summary || {};
  const streams = useMemo(() => workout.streams || {}, [workout]);
  const position = cursor !== null ? streams.position?.[cursor] || null : null;

  const unit = (key) => t(`workouts.activity.units.${key}`);
  const tiles = [
    summary.distance_m != null && { label: t('workouts.activity.distance'), value: formatDistance(summary.distance_m), unit: unit('km') },
    summary.moving_seconds != null && { label: t('workouts.activity.movingTime'), value: formatDuration(summary.moving_seconds) },
    summary.elapsed_seconds != null && summary.elapsed_seconds !== summary.moving_seconds && {
      label: t('workouts.activity.elapsedTime'), value: formatDuration(summary.elapsed_seconds),
    },
    summary.elevation_gain_m != null && { label: t('workouts.activity.elevationGain'), value: summary.elevation_gain_m, unit: unit('m') },
    summary.avg_speed_kmh != null && workout.sport === 'run' && {
      label: t('workouts.activity.avgPace'),
      value: formatPace(summary.avg_speed_kmh), unit: unit('minKm'),
      sub: `${summary.avg_speed_kmh} ${unit('kmh')}`,
    },
    summary.avg_speed_kmh != null && workout.sport !== 'run' && {
      label: t('workouts.activity.avgSpeed'),
      value: summary.avg_speed_kmh, unit: unit('kmh'),
      sub: summary.max_speed_kmh != null ? `${t('workouts.activity.max')} ${summary.max_speed_kmh}` : null,
    },
    summary.avg_power != null && {
      label: t('workouts.activity.avgPower'),
      value: summary.avg_power, unit: t('workouts.units.watts'),
      sub: summary.max_power != null ? `${t('workouts.activity.max')} ${summary.max_power}` : null,
    },
    summary.normalized_power != null && { label: t('workouts.metrics.np'), value: summary.normalized_power, unit: t('workouts.units.watts') },
    summary.intensity_factor != null && {
      label: t('workouts.activity.intensity'),
      value: summary.intensity_factor.toFixed(2),
      sub: `${t('workouts.activity.ftp')} ${summary.ftp} ${t('workouts.units.watts')}`,
    },
    summary.training_stress != null && { label: t('workouts.activity.tss'), value: summary.training_stress },
    summary.work_kj != null && { label: t('workouts.metrics.work'), value: summary.work_kj, unit: t('workouts.units.kj') },
    summary.avg_heart_rate != null && {
      label: t('workouts.activity.avgHeartRate'),
      value: summary.avg_heart_rate, unit: unit('bpm'),
      sub: summary.max_heart_rate != null ? `${t('workouts.activity.max')} ${summary.max_heart_rate}` : null,
    },
    summary.avg_cadence != null && { label: t('workouts.activity.avgCadence'), value: summary.avg_cadence, unit: unit('rpm') },
    summary.calories != null && { label: t('workouts.activity.calories'), value: summary.calories, unit: unit('kcal') },
  ].filter(Boolean);

  const hasTrack = (workout.track || []).length > 1;
  const hasStreams = (streams.time || streams.distance || []).length > 1;

  return (
    <>
      {hasTrack && (
        <section className="workout-card-block">
          <ActivityMap track={workout.track} position={position} />
        </section>
      )}

      <section className="workout-tiles workout-tiles--activity">
        {tiles.map((tile) => (
          <div key={tile.label} className="workout-tile">
            <span className="workout-tile-label">{tile.label}</span>
            <span className="workout-tile-value">
              {tile.value}
              {tile.unit && <span className="workout-tile-unit"> {tile.unit}</span>}
            </span>
            {tile.sub && <span className="workout-tile-sub">{tile.sub}</span>}
          </div>
        ))}
      </section>

      {summary.equipment && (
        <p className="workouts-muted">{t('workouts.activity.equipment')} {summary.equipment}</p>
      )}

      {hasStreams && (
        <section className="workout-card-block">
          <ActivityStreamsChart streams={streams} onHover={setCursor} />
        </section>
      )}

      {(summary.power_curve || []).length > 1 && (
        <section className="workout-card-block">
          <PowerCurveChart best={summary.power_curve} bestLabel={t('workouts.activity.powerCurve')} />
        </section>
      )}
    </>
  );
};

export default ActivityDetails;
